import { useState, useEffect } from 'react'
import { supabase } from './supabaseClient'

const CLUSTERS = ['All', 'Core Product', 'Google Reviews', 'Local SEO', 'WhatsApp Marketing', 'Competitor Comparisons', 'Reputation Management', 'Industry Guides', 'Regional Guides']

export default function Keywords({ onNavigate, addToast }) {
  const [keywords, setKeywords] = useState([])
  const [loading, setLoading] = useState(true)
  const [cluster, setCluster] = useState('All')
  const [showAdd, setShowAdd] = useState(false)
  const [newKw, setNewKw] = useState({ keyword: '', search_volume: '', difficulty: '', cluster: 'Core Product', priority: 'medium', notes: '', content_type: 'cluster' })

  useEffect(() => { loadKeywords() }, [])

  async function loadKeywords() {
    const { data } = await supabase.from('keywords').select('*').order('priority', { ascending: false }).order('created_at', { ascending: false })
    setKeywords(data || [])
    setLoading(false)
  }

  async function addKeyword() {
    if (!newKw.keyword.trim()) { addToast('Enter a keyword', 'error'); return }
    const { error } = await supabase.from('keywords').insert([newKw])
    if (error) { addToast('Failed to add keyword', 'error') }
    else { addToast('Keyword added!'); setShowAdd(false); setNewKw({ keyword: '', search_volume: '', difficulty: '', cluster: 'Core Product', priority: 'medium', notes: '', content_type: 'cluster' }); loadKeywords() }
  }

  async function updateStatus(id, status) {
    await supabase.from('keywords').update({ status }).eq('id', id)
    setKeywords(prev => prev.map(k => k.id === id ? { ...k, status } : k))
  }

  async function deleteKeyword(id) {
    await supabase.from('keywords').delete().eq('id', id)
    setKeywords(prev => prev.filter(k => k.id !== id))
    addToast('Keyword removed')
  }

  const filtered = cluster === 'All' ? keywords : keywords.filter(k => k.cluster === cluster)
  const stats = {
    total: keywords.length,
    pending: keywords.filter(k => k.status === 'pending').length,
    in_progress: keywords.filter(k => k.status === 'in_progress').length,
    published: keywords.filter(k => k.status === 'published').length,
  }

  return (
    <div className="page-content">
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)', marginBottom: 24 }}>
        {[
          { label: 'Total Keywords', value: stats.total },
          { label: 'Pending', value: stats.pending },
          { label: 'In Progress', value: stats.in_progress },
          { label: 'Published', value: stats.published },
        ].map(s => (
          <div className="stat-card" key={s.label}>
            <div className="stat-label">{s.label}</div>
            <div className="stat-value">{s.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', flex: 1 }}>
          {CLUSTERS.map(c => (
            <button key={c} className={`btn btn-sm ${cluster === c ? 'btn-accent' : 'btn-ghost'}`} onClick={() => setCluster(c)}>
              {c}
            </button>
          ))}
        </div>
        <button className="btn btn-accent btn-sm" onClick={() => setShowAdd(!showAdd)}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Add Keyword
        </button>
      </div>

      {showAdd && (
        <div className="panel" style={{ marginBottom: 20 }}>
          <div className="panel-header"><span className="panel-title">Add New Keyword</span></div>
          <div className="panel-body">
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
              <div>
                <label className="form-label">Keyword</label>
                <input className="form-input" placeholder="e.g. google review management" value={newKw.keyword} onChange={e => setNewKw(p => ({ ...p, keyword: e.target.value }))} />
              </div>
              <div>
                <label className="form-label">Volume</label>
                <select className="form-input" value={newKw.search_volume} onChange={e => setNewKw(p => ({ ...p, search_volume: e.target.value }))}>
                  <option value="">—</option>
                  <option>Very High</option><option>High</option><option>Medium</option><option>Low</option>
                </select>
              </div>
              <div>
                <label className="form-label">Difficulty</label>
                <select className="form-input" value={newKw.difficulty} onChange={e => setNewKw(p => ({ ...p, difficulty: e.target.value }))}>
                  <option value="">—</option>
                  <option>Low</option><option>Medium</option><option>High</option>
                </select>
              </div>
              <div>
                <label className="form-label">Cluster</label>
                <select className="form-input" value={newKw.cluster} onChange={e => setNewKw(p => ({ ...p, cluster: e.target.value }))}>
                  {CLUSTERS.filter(c => c !== 'All').map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Priority</label>
                <select className="form-input" value={newKw.priority} onChange={e => setNewKw(p => ({ ...p, priority: e.target.value }))}>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
              <div>
                <label className="form-label">Gen Type</label>
                <select className="form-input" value={newKw.content_type} onChange={e => setNewKw(p => ({ ...p, content_type: e.target.value }))}>
                  <option value="cluster">Cluster</option>
                  <option value="pillar">Pillar</option>
                  <option value="standalone">Standalone</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-accent btn-sm" onClick={addKeyword}>Add Keyword</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowAdd(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="data-table">
        <table>
          <thead>
            <tr>
              <th>Keyword</th>
              <th>Cluster</th>
              <th>Volume</th>
              <th>Difficulty</th>
              <th>Priority</th>
              <th>Gen Type</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr className="loading-row"><td colSpan={8}><span className="spinner" /></td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: 'var(--text3)' }}>No keywords in this cluster</td></tr>
            )}
            {filtered.map(kw => (
              <tr key={kw.id}>
                <td className="td-primary" style={{ fontWeight: 400 }}>{kw.keyword}</td>
                <td style={{ fontSize: 12 }}>{kw.cluster}</td>
                <td style={{ fontSize: 12 }}>{kw.search_volume || '—'}</td>
                <td style={{ fontSize: 12 }}>{kw.difficulty || '—'}</td>
                <td><span className={`badge badge-${kw.priority}`}>{kw.priority}</span></td>
                <td>
                  <span style={{
                    background: kw.content_type === 'pillar' ? '#287af6' : kw.content_type === 'cluster' ? 'rgba(61,200,255,0.15)' : 'transparent',
                    color: kw.content_type === 'pillar' ? '#fff' : kw.content_type === 'cluster' ? '#3dc8ff' : 'var(--text3)',
                    padding: '2px 7px', borderRadius: 4, fontSize: 11, fontWeight: 600
                  }}>
                    {kw.content_type || 'cluster'}
                  </span>
                </td>
                <td><span className={`badge badge-${kw.status}`}>{kw.status.replace('_', ' ')}</span></td>
                <td>
                  <div className="row-actions">
                    <button
                      className="btn btn-sm btn-accent"
                      onClick={() => { onNavigate('new'); setTimeout(() => {}, 100) }}
                      title="Write blog for this keyword"
                    >
                      Write
                    </button>
                    {kw.status === 'pending' && (
                      <button className="btn btn-sm btn-ghost" onClick={() => updateStatus(kw.id, 'in_progress')}>Start</button>
                    )}
                    {kw.status === 'in_progress' && (
                      <button className="btn btn-sm btn-ghost" onClick={() => updateStatus(kw.id, 'published')}>Done</button>
                    )}
                    <button className="btn btn-sm btn-danger" onClick={() => deleteKeyword(kw.id)}>✕</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
